/**
 * useDelayedFlag — anti-flash helper for pending indicators.
 *
 * Rulebook §1: spinner must not appear for short bursts (anti-flash).
 * - Delays appearance by `delayMs` (default 200ms)
 * - Once visible, keeps visible for at least `minDisplayMs` (default 400ms)
 *
 * Usage:
 *   const showSpinner = useDelayedFlag(isPending)
 */
import { useState, useEffect, useRef } from 'react'

export function useDelayedFlag(active: boolean, delayMs = 200, minDisplayMs = 400): boolean {
  const [visible, setVisible] = useState(false)
  const shownAtRef = useRef<number | null>(null)
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const minTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (active) {
      // Start delay timer — show spinner only if still active after delayMs
      delayTimerRef.current = setTimeout(() => {
        setVisible(true)
        shownAtRef.current = Date.now()
      }, delayMs)
    } else {
      // Clear pending delay timer (never shown)
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current)
        delayTimerRef.current = null
      }

      if (shownAtRef.current !== null) {
        // Spinner is visible — enforce min-display before hiding
        const elapsed = Date.now() - shownAtRef.current
        const remaining = minDisplayMs - elapsed
        const delay = remaining > 0 ? remaining : 0
        minTimerRef.current = setTimeout(() => {
          setVisible(false)
          shownAtRef.current = null
        }, delay)
      }
    }

    return () => {
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current)
      if (minTimerRef.current) clearTimeout(minTimerRef.current)
    }
  }, [active, delayMs, minDisplayMs])

  return visible
}
